import { useState, type ReactNode } from "react";
import { TextField } from "@/components/TextField";
import { formatMoneyInput, parseMoneyInput } from "@/lib/currency";

// BRK-S142 — the three NUMERIC controls, in one component.
//
// The bond renderer used to draw `currency`, `number` and `percent` from one
// branch: `<input type="number">` with the field's bounds. It satisfied D-10
// rule 1 (the control matches the type) and quietly broke rule 3 — a number
// input cannot render a thousands separator, so a broker entering a seven-figure
// contract price read `2500000` and counted zeros, and a percent field looked
// exactly like a dollar field.
//
// So the three are told apart here, and the differences are the ones a broker
// can see:
//
//   • CURRENCY  — a `$` inside the box, and the value GROUPED as they type.
//     Necessarily `type="text"` with `inputMode="decimal"`: a number input
//     refuses to display a comma, so grouping and `type="number"` are mutually
//     exclusive and the separator is the half that matters here.
//   • PERCENT   — a `%` inside the box, bounded 0–100 by the SAME default the
//     validator applies (`fieldError`), so the control and the rule cannot
//     disagree about what the field accepts.
//   • NUMBER    — a plain numeric input. A count (maintenance months) rather
//     than an amount: no symbol, no grouping, and its bounds come only from the
//     configuration.
//
// ONE component rather than three, because everything else about them is
// identical — the label, the marker, the inline error, the "empty means
// undefined, and 0 is an answer" rule — and three near-copies is where that
// rule would drift.

export type NumericKind = "currency" | "percent" | "number";

export function NumericField({
  kind,
  label,
  value,
  onChange,
  required,
  optional,
  error,
  min,
  max,
  unitControl,
}: {
  kind: NumericKind;
  label: string;
  value: number | undefined;
  /** `undefined` for an empty field — never 0, which is an answer. */
  onChange: (value: number | undefined) => void;
  required?: boolean;
  optional?: boolean;
  error?: string;
  min?: number;
  max?: number;
  /**
   * A control that IS the unit indicator, drawn inside the box as its suffix
   * (BRK-S181 — the bond penalty's % / $ toggle). When present it REPLACES the
   * static `$` / `%` adornment: one unit indicator, not two.
   */
  unitControl?: ReactNode;
}) {
  /**
   * What the broker has typed, while they are typing it — `null` once they are
   * done, meaning "show the formatted value".
   *
   * A currency field REFORMATS the value it holds, and reformatting on every
   * keystroke fights the typist: `1234.` parses to 1234, formats back to
   * `1,234`, and the decimal point the broker just pressed disappears. So the
   * raw text stands until blur, and the grouped form is what they read
   * afterwards. Only currency needs it; a number input has no formatting to
   * fight.
   */
  const [draft, setDraft] = useState<string | null>(null);

  if (kind === "currency") {
    return (
      <TextField
        label={label}
        // Not `type="number"`: that control will not render the separator, and
        // the separator is the whole reason this branch exists.
        type="text"
        inputMode="decimal"
        adornStart={unitControl === undefined ? "$" : undefined}
        endControl={unitControl}
        value={draft ?? (value === undefined ? "" : formatMoneyInput(value))}
        onChange={(e) => {
          setDraft(e.target.value);
          onChange(parseMoneyInput(e.target.value));
        }}
        // Back to the grouped form, from the NUMBER rather than from the text —
        // so what the broker reads afterwards is exactly the figure that will
        // be sent (D-10's split, checked in both directions).
        onBlur={() => setDraft(null)}
        required={required}
        optional={optional}
        error={error}
      />
    );
  }

  const isPercent = kind === "percent";
  return (
    <TextField
      label={label}
      type="number"
      adornEnd={isPercent && unitControl === undefined ? "%" : undefined}
      endControl={unitControl}
      // A percent is 0–100 by its own nature; explicit bounds still win. The
      // same defaults `fieldError` applies, so the control and the validator
      // cannot disagree.
      min={min ?? (isPercent ? 0 : undefined)}
      max={max ?? (isPercent ? 100 : undefined)}
      value={value === undefined ? "" : value}
      onChange={(e) => {
        const n = Number(e.target.value);
        onChange(e.target.value === "" || Number.isNaN(n) ? undefined : n);
      }}
      required={required}
      optional={optional}
      error={error}
    />
  );
}
